import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAccountType, getBoardLimit } from '../context/accountUtils';
import api from '../services/api';
import { showToast } from '../context/ToastHandle';
import { useConfirm } from './ConfirmPopup';
import { clearBoardCaches } from '../context/cacheUtils';
import Popup from './Popup';
import SettingsDrawer from './SettingsDrawer';

const Header = ({ onToggleSentenceBuilder, showSentenceBuilder }) => {
  const { logout, user } = useAuth();
  const { showConfirm } = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();

  const isBoardView = /^\/board\/\d+$/.test(location.pathname);
  const isBoardEdit = /^\/board\/\d+\/edit$/.test(location.pathname);
  const match = location.pathname.match(/^\/board\/(\d+)/);
  const currentBoardId = match ? match[1] : null;
  const isUnlimited = getAccountType() === 'unlimited';

  const [isHeaderOpen, setHeaderOpen] = useState(!isBoardView || isBoardEdit);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [showPinPopup, setShowPinPopup] = useState(false);
  const [enteredPin, setEnteredPin] = useState('');
  const pinContainerRef = useRef(null);

  const [coreSettings, setCoreSettings] = useState(null);
  const [showCoreSwitcher, setShowCoreSwitcher] = useState(false);
  const [switchingCore, setSwitchingCore] = useState(false);

  const [editBoards, setEditBoards] = useState([]);
  const [currentBoardName, setCurrentBoardName] = useState('');
  const [showAddBoardPopup, setShowAddBoardPopup] = useState(false);
  const [boardActionBusy, setBoardActionBusy] = useState(false);

  const stickyCoreCacheKey = () => {
    try {
      const token = localStorage.getItem('token');
      const payload = token ? JSON.parse(atob(token.split('.')[1])) : null;
      return `stickyCoreSettings:${payload?.sub || 'current'}`;
    } catch {
      return 'stickyCoreSettings:current';
    }
  };

  const saveCoreCache = (data) => {
    try {
      localStorage.setItem(stickyCoreCacheKey(), JSON.stringify(data));
    } catch {}
  };

  const loadCoreSettings = async () => {
    if (!isUnlimited || (!isBoardView && !isBoardEdit)) {
      setCoreSettings(null);
      return;
    }

    if (!navigator.onLine) {
      try {
        const cached = JSON.parse(localStorage.getItem(stickyCoreCacheKey()) || 'null');
        if (cached) setCoreSettings(cached);
      } catch {}
      return;
    }

    try {
      const { data } = await api.get('/core-settings.php');
      setCoreSettings(data);
      saveCoreCache(data);
    } catch (err) {
      try {
        const cached = JSON.parse(localStorage.getItem(stickyCoreCacheKey()) || 'null');
        if (cached) setCoreSettings(cached);
      } catch {}
      console.debug('Could not load Sticky Core header settings:', err);
    }
  };

  const loadEditBoards = async () => {
    if (!navigator.onLine) return [];
    try {
      const { data } = await api.get('/boards.php');
      const list = Array.isArray(data) ? data : [];
      setEditBoards(list);
      if (currentBoardId) {
        const current = list.find((b) => String(b.id) === String(currentBoardId));
        setCurrentBoardName(current?.name || `Board #${currentBoardId}`);
      }
      return list;
    } catch (err) {
      console.debug('Could not load boards for header:', err);
      return [];
    }
  };

  useEffect(() => {
    if (isBoardEdit) setHeaderOpen(true);
    else if (!isBoardView) setHeaderOpen(true);
  }, [isBoardView, isBoardEdit, location.pathname]);

  useEffect(() => {
    loadCoreSettings();

    const refresh = (event) => {
      if (event?.detail) {
        setCoreSettings(event.detail);
        saveCoreCache(event.detail);
      } else {
        loadCoreSettings();
      }
    };

    window.addEventListener('sticky-core-changed', refresh);
    return () => window.removeEventListener('sticky-core-changed', refresh);
  }, [location.pathname, isUnlimited]);

  useEffect(() => {
    if (isBoardEdit) loadEditBoards();
    else {
      setEditBoards([]);
      setCurrentBoardName('');
    }
  }, [location.pathname]);

  const requestOpenHeader = () => {
    if (isHeaderOpen) {
      setSettingsOpen(false);
      setHeaderOpen(false);
      return;
    }

    const expectedPin = user?.pin?.toString?.() || '';
    if (isBoardView && expectedPin) {
      setEnteredPin('');
      setShowPinPopup(true);
    } else {
      setHeaderOpen(true);
    }
  };

  const handlePinInput = (digit) => {
    if (enteredPin.length >= 4) return;
    const nextPin = enteredPin + digit;
    setEnteredPin(nextPin);

    if (nextPin.length === 4) {
      if (nextPin === user?.pin?.toString()) {
        setHeaderOpen(true);
        setShowPinPopup(false);
        setEnteredPin('');
      } else {
        pinContainerRef.current?.classList.add('shake-red');
        setTimeout(() => pinContainerRef.current?.classList.remove('shake-red'), 400);
        setEnteredPin('');
      }
    }
  };

  const switchCoreBoard = async (boardId) => {
    if (switchingCore) return;
    setSwitchingCore(true);

    try {
      let data;
      if (!navigator.onLine) {
        const selectedBoard = (coreSettings?.core_boards || []).find(
          (board) => String(board.id) === String(boardId)
        ) || null;
        data = {
          ...coreSettings,
          enabled: true,
          board_id: Number(boardId),
          side: coreSettings?.side || 'left',
          selected_board: selectedBoard,
        };
      } else {
        const response = await api.put('/core-settings.php', {
          action: 'settings',
          enabled: true,
          board_id: Number(boardId),
          side: coreSettings?.side || 'left',
        });
        data = response.data;
      }

      setCoreSettings(data);
      saveCoreCache(data);
      setShowCoreSwitcher(false);
      window.dispatchEvent(new CustomEvent('sticky-core-changed', { detail: data }));
    } catch (err) {
      showToast(err?.response?.data?.message || 'Could not switch Core Board.', 'error', false);
    } finally {
      setSwitchingCore(false);
    }
  };

  const handleFinishEditing = async () => {
    if (!currentBoardId) return;

    if (!isUnlimited) {
      navigate(`/board/${currentBoardId}`);
      return;
    }

    try {
      let settings;
      if (!navigator.onLine) {
        try {
          settings = JSON.parse(localStorage.getItem(stickyCoreCacheKey()) || 'null');
        } catch {
          settings = null;
        }
      } else {
        const response = await api.get('/core-settings.php');
        settings = response.data;
      }

      const currentCoreBoard = (settings?.core_boards || []).find(
        (board) => String(board.id) === String(currentBoardId)
      );

      if (currentCoreBoard?.core_default_board_id) {
        let updatedSettings;

        if (!navigator.onLine) {
          updatedSettings = {
            ...settings,
            enabled: true,
            board_id: Number(currentBoardId),
            side: settings?.side || 'left',
            selected_board: currentCoreBoard,
          };
        } else {
          const response = await api.put('/core-settings.php', {
            action: 'settings',
            enabled: true,
            board_id: Number(currentBoardId),
            side: settings?.side || 'left',
          });
          updatedSettings = response.data;
        }

        setCoreSettings(updatedSettings);
        saveCoreCache(updatedSettings);
        window.dispatchEvent(new CustomEvent('sticky-core-changed', { detail: updatedSettings }));
        navigate(`/board/${currentCoreBoard.core_default_board_id}`);
        return;
      }
    } catch (err) {
      console.debug('Could not resolve Core Board default when finishing editing:', err);
    }

    navigate(`/board/${currentBoardId}`);
  };

  const enterEditMode = async () => {
    if (!navigator.onLine) {
      showToast('Edit mode is not available while offline right now. Please reconnect and try again.', 'warning', false);
      return false;
    }

    if (currentBoardId) {
      navigate(`/board/${currentBoardId}/edit`);
      return true;
    }

    const boards = await loadEditBoards();
    if (!boards.length) {
      showToast('You do not have a board to edit yet. Create a board first.', 'info', false);
      return false;
    }

    navigate(`/board/${boards[0].id}/edit`);
    return true;
  };

  const invalidateBoardListCache = async () => {
    if (!navigator.onLine) return;
    try {
      const cache = await caches.open('api-cache');
      await Promise.all([
        cache.delete('/aac/api/boards.php'),
        cache.delete('/aac/api/folders.php'),
        cache.delete('/aac/api/offline-sync.php'),
      ]);
    } catch {}
  };

  const openAddBoard = async () => {
    if (!navigator.onLine) {
      showToast('Boards cannot be created while offline. Please reconnect and try again.', 'warning', false);
      return;
    }
    const boards = await loadEditBoards();
    if (boards.length >= getBoardLimit()) {
      showToast(`Board limit reached (${getBoardLimit()}). Upgrade your plan to add more.`, 'error', false);
      return;
    }
    setShowAddBoardPopup(true);
  };

  const createBoardFromHeader = async (boardData) => {
    if (boardActionBusy) return;
    setBoardActionBusy(true);
    try {
      const boards = await loadEditBoards();
      if (boards.length >= getBoardLimit()) {
        showToast(`Board limit reached (${getBoardLimit()}). Upgrade your plan to add more.`, 'error', false);
        return;
      }

      const createRes = await api.post('/boards.php', boardData);
      const newId = Number(createRes?.data?.id);
      if (!newId) throw new Error('Board was created but no board ID was returned.');

      if (boardData.is_core_board && isUnlimited) {
        await api.put('/core-settings.php', {
          action: 'set_board_core',
          board_id: newId,
          is_core_board: true,
          core_default_board_id: boardData.core_default_board_id || null,
        });
        window.dispatchEvent(new CustomEvent('sticky-core-changed'));
      }

      await invalidateBoardListCache();
      setShowAddBoardPopup(false);
      navigate(`/board/${newId}/edit`);
    } catch (err) {
      showToast(err?.response?.data?.message || err?.response?.data?.error || err.message || 'Could not create board.', 'error', false);
    } finally {
      setBoardActionBusy(false);
    }
  };

  const duplicateCurrentBoard = async () => {
    if (!currentBoardId || boardActionBusy) return;
    if (!navigator.onLine) {
      showToast('Boards cannot be duplicated while offline. Please reconnect and try again.', 'warning', false);
      return;
    }

    setBoardActionBusy(true);
    try {
      const boards = await loadEditBoards();
      if (boards.length >= getBoardLimit()) {
        showToast(`Board limit reached (${getBoardLimit()}). Upgrade your plan to duplicate this board.`, 'error', false);
        return;
      }

      const res = await api.post(`/boards.php?duplicate=${currentBoardId}`);
      const newId = Number(res?.data?.id);
      if (!newId) throw new Error('No duplicated board ID was returned.');
      await invalidateBoardListCache();
      navigate(`/board/${newId}/edit`);
      showToast('Board duplicated.', 'success', false);
    } catch (err) {
      showToast(err?.response?.data?.error || 'Could not duplicate board.', 'error', false);
    } finally {
      setBoardActionBusy(false);
    }
  };

  const deleteCurrentBoard = async () => {
    if (!currentBoardId || boardActionBusy) return;
    if (!navigator.onLine) {
      showToast('Boards cannot be deleted while offline. Please reconnect and try again.', 'warning', false);
      return;
    }

    const confirmed = await showConfirm(`Delete ${currentBoardName || 'this board'}? This cannot be undone.`);
    if (!confirmed) return;

    setBoardActionBusy(true);
    try {
      const before = editBoards.length ? editBoards : await loadEditBoards();
      await api.delete(`/boards.php?id=${currentBoardId}`);
      await clearBoardCaches(currentBoardId);
      await invalidateBoardListCache();
      window.dispatchEvent(new CustomEvent('sticky-core-changed'));

      const remaining = before.filter((b) => String(b.id) !== String(currentBoardId));
      if (remaining.length) {
        navigate(`/board/${remaining[0].id}/edit`);
      } else {
        navigate('/boards');
      }
      showToast('Board deleted.', 'success', false);
    } catch (err) {
      showToast(err?.response?.data?.error || 'Could not delete board.', 'error', false);
    } finally {
      setBoardActionBusy(false);
    }
  };

  const openBoardSelector = () => {
    window.dispatchEvent(new CustomEvent('iboard-open-board-selector'));
  };

  const handleLogout = () => {
    setSettingsOpen(false);
    logout();
    navigate('/login');
  };

  const iconButton = {
    border: '1px solid #d6d9de',
    borderRadius: 9,
    background: '#fff',
    color: '#24292f',
    minHeight: 34,
    padding: '6px 9px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  const actionButton = {
    ...iconButton,
    background: '#f8f9fb',
  };

  return (
    <>
      <header
        className={`app-modern-header ${isHeaderOpen ? 'is-open' : 'is-closed'}${isBoardEdit ? ' is-edit-mode' : ''}`}
        style={
          isBoardEdit
            ? {
                background: '#e4e9f0',
                borderBottom: '1px solid #a1b3c8',
                boxShadow: '0 1px 4px rgba(15, 23, 42, 0.06)',
              }
            : undefined
        }
      >
        <div className="app-modern-header-left">
          <button
            type="button"
            style={iconButton}
            onClick={requestOpenHeader}
            title={isHeaderOpen ? 'Close header' : 'Open header'}
            aria-label={isHeaderOpen ? 'Close header' : 'Open header'}
          >
            {isHeaderOpen ? '▴' : '▾'}
          </button>

          {isHeaderOpen && location.pathname !== '/boards' && (
            <button
              type="button"
              className="header-home-button"
              onClick={() => {
                setSettingsOpen(false);
                navigate('/boards');
              }}
              title="Home"
              aria-label="Go to boards"
            >
              🏠
            </button>
          )}

          {isBoardView && onToggleSentenceBuilder && (
            <button
              type="button"
              style={{
                ...iconButton,
                background: showSentenceBuilder ? '#e7f5ea' : '#fff',
                borderColor: showSentenceBuilder ? '#8ac79a' : '#d6d9de',
                color: showSentenceBuilder ? '#216e35' : '#24292f',
              }}
              onClick={onToggleSentenceBuilder}
              aria-pressed={showSentenceBuilder}
            >
              💬 {isHeaderOpen ? `Sentence ${showSentenceBuilder ? 'On' : 'Off'}` : ''}
            </button>
          )}

          {isBoardView && isUnlimited && coreSettings?.enabled && coreSettings?.core_boards?.length > 0 && (
            <button
              type="button"
              style={{
                ...iconButton,
                background: '#f5f0ff',
                borderColor: '#c9b4f4',
                color: '#5b35a5',
              }}
              onClick={() => setShowCoreSwitcher(true)}
              title="Switch Core Board"
            >
              🔄 {isHeaderOpen ? `CB: ${coreSettings?.selected_board?.name || 'Switch'}` : 'CB'}
            </button>
          )}

          {isBoardEdit && isHeaderOpen && (
            <>
              <div
                aria-label="Editing mode active"
                title="Editing mode"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 7,
                  minHeight: 34,
                  padding: '5px 10px',
                  borderRadius: 8,
                  background: '#eef4ff',
                  border: '1px solid #c7d7f5',
                  color: '#274c77',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.01em',
                  whiteSpace: 'nowrap',
                  boxSizing: 'border-box',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: '#3b82f6',
                    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.10)',
                    flex: '0 0 auto',
                  }}
                />
                Editing
              </div>

              <button
                type="button"
                className="edit-board-name-button"
                onClick={openBoardSelector}
                title="Switch board"
              >
                <span className="edit-board-name-text">{currentBoardName || `Board #${currentBoardId}`}</span>
                <span aria-hidden="true">▾</span>
              </button>

              <button
                type="button"
                className="edit-header-action edit-header-finish"
                onClick={handleFinishEditing}
              >
                ✓ Finish Editing
              </button>

              <button
                type="button"
                className="edit-header-action edit-header-add"
                onClick={openAddBoard}
                disabled={boardActionBusy}
              >
                ＋ Add Board
              </button>

              <button
                type="button"
                className="edit-header-action"
                onClick={duplicateCurrentBoard}
                disabled={boardActionBusy}
              >
                ⧉ Duplicate
              </button>

              <button
                type="button"
                className="edit-header-action edit-header-delete"
                onClick={deleteCurrentBoard}
                disabled={boardActionBusy}
              >
                🗑 Delete
              </button>
            </>
          )}
        </div>

        {isHeaderOpen && (
          <div className="app-modern-header-right">
            <button
              type="button"
              className="settings-menu-button"
              onClick={() => setSettingsOpen(true)}
              title="Settings"
              aria-label="Open settings"
            >
              ☰
            </button>
          </div>
        )}
      </header>

      <SettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onEnterEditMode={enterEditMode}
        onLogout={handleLogout}
      />

      {showCoreSwitcher && (
        <div className="core-switcher-backdrop" onMouseDown={() => setShowCoreSwitcher(false)}>
          <div className="core-switcher-card" onMouseDown={(e) => e.stopPropagation()}>
            <div className="core-switcher-title-row">
              <div>
                <div className="core-switcher-title">Switch Core Board</div>
                <div className="core-switcher-subtitle">The current content board stays open.</div>
              </div>
              <button type="button" className="plain-close-button" onClick={() => setShowCoreSwitcher(false)}>×</button>
            </div>
            <div className="core-switcher-list">
              {(coreSettings?.core_boards || []).map((board) => {
                const selected = String(board.id) === String(coreSettings?.board_id);
                return (
                  <button
                    key={board.id}
                    type="button"
                    disabled={switchingCore || selected}
                    onClick={() => switchCoreBoard(board.id)}
                    className={`core-switcher-option${selected ? ' is-selected' : ''}`}
                  >
                    <span>{board.name}</span>
                    {selected && <span>Current</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {showPinPopup && (
        <div
          className="pin-modern-backdrop"
          onMouseDown={() => {
            setShowPinPopup(false);
            setEnteredPin('');
          }}
        >
          <div className="pin-modern-card" onMouseDown={(e) => e.stopPropagation()}>
            <div ref={pinContainerRef} className="pin-modern-boxes">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className={`pin-modern-box${i < enteredPin.length ? ' is-filled' : ''}`}>
                  {enteredPin[i] || ''}
                </div>
              ))}
            </div>
            <div className="pin-modern-pad">
              {[1,2,3,4,5,6,7,8,9,'⌫',0,'✖'].map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    if (key === '⌫') setEnteredPin((prev) => prev.slice(0, -1));
                    else if (key === '✖') {
                      setEnteredPin('');
                      setShowPinPopup(false);
                    } else handlePinInput(String(key));
                  }}
                >
                  {key}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <Popup
        isOpen={showAddBoardPopup}
        onClose={() => setShowAddBoardPopup(false)}
        availableBoards={editBoards}
        onSubmit={createBoardFromHeader}
      />
    </>
  );
};

export default Header;
